/*
 * Copyright 2023 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { SampleDatasetTypes } from "../../components/dataset-ingestion-flow/connection-type/connection-type-picker/connection-type-card/connection-type-card.interfaces";
import { DatasetIngestionWizardStep } from "../../components/dataset-ingestion-flow/dataset-ingestion-flow.interfaces";
import { IngestionFormats } from "../../rest/dto/datasets.interfaces";
import {
    CategoryTypes,
    Connection,
    ConnectionConfig,
    ConnectionTypes,
    IngestionLocator,
    SchemaResponseType,
    SnowflakeConnectionConfig,
    SqlIngestionSettings,
} from "../../rest/dto/datasources.interfaces";
import { Namespace } from "../../rest/dto/namespaces.interfaces";

export type DatasetIngestionState = DatasetIngestionActions &
    DatasetIngestionProps;

export interface FileStorageState {
    fileBase64: string | null;
    setFileBase64: (encodedFile: string | null) => void;
}

// Store Actions
export interface DatasetIngestionActions {
    resetStore: () => void;
    setActiveStep: (step: DatasetIngestionWizardStep) => void;
    setConnectionValue: (
        property: keyof ConnectionState,
        value: ConnectionState[keyof ConnectionState]
    ) => void;
    setConnectionValues: (value: ConnectionState) => void;
    setDatasetValue: (
        property: keyof DatasetState,
        value: DatasetState[keyof DatasetState]
    ) => void;
    setDatasetValues: (value: DatasetState) => void;
    setDefaultNameGenerator: (value: string | undefined) => void;
    setInputSchema: (inputSchema: InputSchema | null) => void;
    setOutputSchema: (outputSchema: OutputSchema | null) => void;
    setPinotConnection: (connection: Connection | null) => void;
    setProgressStep: (step: DatasetIngestionWizardStep) => void;
    setPrimaryKeyColumns: (primaryKeyColumns: string[]) => void;
    setSampleDatasetId: (sampleDatasetId: SampleDatasetTypes | null) => void;
    setTableConfig: (
        configType: TableConfigTypes,
        tableConfig?: DatasetConfig | null
    ) => void;
}

// Store Props
export interface DatasetIngestionProps {
    config: Config;
    connection: ConnectionState;
    dataset: DatasetState;
    pinotConnection: Connection | null;
    sampleDatasetId: SampleDatasetTypes | null;
    schema: {
        input: InputSchema | null;
        output: OutputSchema | null;
    };
    steps: {
        active: DatasetIngestionWizardStep;
        progress: DatasetIngestionWizardStep; // furthest step reached by User
    };
}

export interface ConnectionState {
    authMethod: string | null;
    category: CategoryTypes | null;
    description: string;
    isExistingConnection: boolean;
    name: string;
    properties: ConnectionConfig | null;
    srn: string | null;
    type: ConnectionTypes | null;
}

export interface DatasetState {
    description: string;
    ingestionFormat: IngestionFormats | null;
    ingestionLocator: IngestionLocator | null;
    name: string;
    namespace: Namespace;
    tableType: TableTypes;
    timeColumn: DateTimeSpec | null;
    uploadFile: { name: string } | null;
}

// enums
export enum CronSyntax {
    DAILY = "0 0 4 ? * * *",
    HOURLY = "0 20 * ? * * *",
}

export enum DataTypes {
    BIG_DECIMAL = "BIG_DECIMAL",
    BOOLEAN = "BOOLEAN",
    BYTES = "BYTES",
    DOUBLE = "DOUBLE",
    FLOAT = "FLOAT",
    INT = "INT",
    JSON = "JSON",
    LONG = "LONG",
    STRING = "STRING",
    TIMESTAMP = "TIMESTAMP",
}

export const NumberDataTypes = [
    DataTypes.INT,
    DataTypes.LONG,
    DataTypes.DOUBLE,
    DataTypes.FLOAT,
    DataTypes.BIG_DECIMAL,
];

export enum DateTimeDataTypes {
    TIMESTAMP = DataTypes.TIMESTAMP,
    LONG = DataTypes.LONG,
    STRING = DataTypes.STRING,
}

export enum DimensionDataTypes {
    BIG_DECIMAL = DataTypes.BIG_DECIMAL,
    BOOLEAN = DataTypes.BOOLEAN,
    BYTES = DataTypes.BYTES,
    DOUBLE = DataTypes.DOUBLE,
    FLOAT = DataTypes.FLOAT,
    INT = DataTypes.INT,
    JSON = DataTypes.JSON,
    LONG = DataTypes.LONG,
    STRING = DataTypes.STRING,
    TIMESTAMP = DataTypes.TIMESTAMP,
}

export enum MetricDataTypes {
    BIG_DECIMAL = DataTypes.BIG_DECIMAL,
    BYTES = DataTypes.BYTES,
    DOUBLE = DataTypes.DOUBLE,
    FLOAT = DataTypes.FLOAT,
    INT = DataTypes.INT,
    LONG = DataTypes.LONG,
}

export enum SchemaFieldTypes {
    DATETIME = "DATE_TIME",
    DIMENSION = "DIMENSION",
    METRIC = "METRIC",
}

export const DataTypesForFieldTypes: Record<SchemaFieldTypes, DataTypes[]> = {
    [SchemaFieldTypes.DIMENSION]: Object.values(
        DimensionDataTypes
    ) as DataTypes[],
    [SchemaFieldTypes.METRIC]: Object.values(MetricDataTypes) as DataTypes[],
    [SchemaFieldTypes.DATETIME]: Object.values(
        DateTimeDataTypes
    ) as DataTypes[],
};

export enum DateTimeFormats {
    EPOCH = "millisecondsSinceEpoch",
    ISO_FORMAT = "ISO_FORMAT",
}

export enum FileIngestionTaskConfigProps {
    ACCESS_KEY = "input.fs.prop.accessKey",
    CLASS_NAME = "input.fs.className",
    FORMAT = "inputFormat",
    INPUT_DIR = "inputDirURI",
    REGION = "input.fs.prop.region",
    SECRET_KEY = "input.fs.prop.secretKey",
}

export enum RetentionFormats {
    DAYS = "DAYS",
    HOURS = "HOURS",
}

export enum TableTypes {
    DIMENSION = "DIMENSION",
    STANDARD = "STANDARD",
}

export enum TaskConfigs {
    FILE_INGESTION = "FileIngestionTask",
    REALTIME_TO_OFFLINE = "RealtimeToOfflineSegmentsTask",
    SEGMENT_IMPORT = "SegmentImportTask",
    SQL_BATCH_PUSH = "SqlConnectorBatchPushTask",
}

export enum TableConfigTypes {
    OFFLINE = "OFFLINE",
    REALTIME = "REALTIME",
}

export enum TimeUnits {
    DEFAULT = "",
    HOURS = "HOURS",
    MILLISECONDS = "MILLISECONDS",
    MINUTES = "MINUTES",
    SECONDS = "SECONDS",
}

export interface BaseSchemaFieldSpec {
    dataType: DataTypes;
    /** The default value to use if the incoming value from the data source is `null` */
    defaultNullValue?: string | number | boolean;
    fieldType: SchemaFieldTypes;
    name: string;
    maxLength?: number;
    virtualColumnProvider?: unknown;
}

export interface Config {
    defaultSegmentNameGenerator: string | undefined;
    offline?: DatasetConfig | null;
    realtime: DatasetConfig | null;
}

export interface ConfigFieldSpec {
    name: string;
    encodingType: string;
    indexType: string;
}

export interface DatasetConfig {
    description: string | undefined;
    config: TableConfig | undefined;
    type: string | undefined;
}

export interface DateTimeSpec extends BaseSchemaFieldSpec {
    fieldType: SchemaFieldTypes.DATETIME;
    format: string;
    granularity: string;
}

export interface DimensionSpec extends BaseSchemaFieldSpec {
    fieldType: SchemaFieldTypes.DIMENSION;
    singleValueField?: boolean;
}

export interface MetricSpec extends BaseSchemaFieldSpec {
    fieldType: SchemaFieldTypes.METRIC;
}

export type SchemaFieldSpec = DateTimeSpec | DimensionSpec | MetricSpec;

export type FileIngestionTaskType = {
    [key in FileIngestionTaskConfigProps]: string;
} & {
    enableSync?: boolean;
    schedule: CronSyntax;
    includeFileNamePattern?: string;
};

export interface IngestionConfigType {
    batchIngestionConfig?: {
        [key: string]: string;
    };
    streamIngestionConfig?: {
        streamConfigMaps: Record<string, string>[];
    };
    transformConfigs?: { [name: string]: string }[];
    filterConfig?: { filterFunction?: string };
}

// Is not always JSON object
export interface InputSchema extends Omit<SchemaResponseType, "schema"> {
    schema: ParsedInputSchema | string;
}

export interface OutputSchema extends Omit<SchemaResponseType, "schema"> {
    schema: ParsedOutputSchema;
}

export enum OutputSchemaType {
    // Add the schema types here as and when used
    NONE = "NONE",
}

export interface ParsedInputSchema {
    $schema: string;
    type: string;
    properties: {
        anyOf?: {
            type: string;
            items: {
                type: string;
            };
        };
        type?: string | string[];
    };
}

export interface ParsedOutputSchema {
    mappings: { [column: string]: string };
    schema: {
        schemaName: string;
        primaryKeyColumns?: string[];
    } & SchemaFieldSpecs;
    filter?: {
        filterFunction?: string;
    };
}

export interface RealtimeToOfflineSegmentsTaskType {
    schedule: CronSyntax;
}

export interface SchemaFieldSpecs {
    dateTimeFieldSpecs?: DateTimeSpec[];
    dimensionFieldSpecs?: DimensionSpec[];
    metricFieldSpecs?: MetricSpec[]; // MetricSpec has no differentiating keys
}

export enum SegmentPushTypes {
    APPEND = "APPEND",
    REFRESH = "REFRESH",
}

export interface SegmentsConfigDataType {
    replicasPerPartition?: string;
    replication: string;
    retentionTimeUnit?: RetentionFormats;
    retentionTimeValue?: string;
    deletedSegmentsRetentionPeriod?: string;
    schemaName: string;
    segmentPushType: SegmentPushTypes;
    timeColumnName: string;
}

export interface SegmentImportTaskType {
    schedule: CronSyntax;
}

export type SqlConnectorBatchPushTaskType = {
    [key in SnowflakeConnectionConfig]: string;
} & {
    [SqlIngestionSettings.NUM_BUCKETS_TO_LOOKBACK]?: string;
    schedule: CronSyntax;
} & Record<string, string>;

export interface StarTreeIndexConfigType {
    dimensionsSplitOrder: string[];
    skipStarNodeCreationForDimensions: string[];
    functionColumnPairs?: string[];
    maxLeafRecords?: number;
}

export interface TableConfig {
    fieldConfigList?: ConfigFieldSpec[];
    ingestionConfig?: IngestionConfigType;
    isDimTable: boolean;
    routing?: {
        segmentPrunerTypes: ["partition"];
        instanceSelectorType: "strictReplicaGroup";
    };
    metadata: Record<string, unknown>;
    segmentsConfig: SegmentsConfigDataType;
    tableIndexConfig: TableIndexConfigType;
    tableName: string;
    tableType: TableConfigTypes;
    task?: TaskType;
    tenants: Tenants;
    upsertConfig?: UpsertConfigType;
}

export interface TableIndexConfigType {
    aggregateMetrics: boolean;
    autoGeneratedInvertedIndex: boolean;
    bloomFilterColumns?: string[];
    createInvertedIndexDuringSegmentGeneration: boolean;
    enableDefaultStarTree: boolean;
    enableDynamicStarTreeCreation: boolean;
    invertedIndexColumns?: string[];
    jsonIndexColumns?: string[];
    loadMode: string;
    noDictionaryColumns?: string[];
    nullHandlingEnabled: boolean;
    onHeapDictionaryColumns?: string[];
    rangeIndexColumns?: string[];
    rangeIndexVersion: number;
    segmentNameGeneratorType?: string;
    sortedColumn?: string[];
    starTreeIndexConfigs?: StarTreeIndexConfigType[];
    varLengthDictionaryColumns?: string[];
}

// TODO: Move all Task- interfaces to additional-config interfaces
export interface TaskType {
    taskTypeConfigsMap: TaskTypeConfigsMap;
}

export interface TaskTypeConfigsMap {
    [TaskConfigs.FILE_INGESTION]?: FileIngestionTaskType;
    [TaskConfigs.REALTIME_TO_OFFLINE]?: RealtimeToOfflineSegmentsTaskType;
    [TaskConfigs.SEGMENT_IMPORT]?: SegmentImportTaskType;
    [TaskConfigs.SQL_BATCH_PUSH]?: SqlConnectorBatchPushTaskType;
}

export interface Tenants {
    server: string;
    broker: string;
}

export interface UpsertConfigType {
    hashFunction: string;
    mode: string;
}

export enum IngestionDataType {
    JSON = "JSON",
    PARQUET = "PARQUET",
    AVRO = "AVRO",
    CSV = "CSV",
    ORC = "ORC",
}
